import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { Swords } from 'lucide-react';
import { PageTitle } from '../../components/layout/PageTitle';
import { useI18n } from '../../contexts/I18nContext';

export function Queue() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [inQueue, setInQueue] = useState(false);
  const { t, getPathForLang } = useI18n();

  const createMatch = async (player1Id: string, player2Id: string) => {
    try {
      // Vérifier si un des joueurs a déjà une partie en cours
      const { data: existingMatches, error: checkError } = await supabase
        .from('matches')
        .select('id')
        .or(`player1_id.eq.${player1Id},player2_id.eq.${player1Id},player1_id.eq.${player2Id},player2_id.eq.${player2Id}`)
        .in('status', ['pending', 'active'])
        .limit(1);

      if (checkError) throw checkError;

      // Si un des joueurs a déjà une partie, on annule la création
      if (existingMatches && existingMatches.length > 0) {
        // Nettoyer la file d'attente pour les deux joueurs
        await supabase
          .from('queue')
          .delete()
          .in('player_id', [player1Id, player2Id]);
        
        console.log(t('pvp_queue.errors.player_in_match'));
        return null;
      }

      // Sinon, on crée la nouvelle partie
      const password = Math.random().toString(36).substring(2, 12);
      
      const { error: matchError } = await supabase
        .from('matches')
        .insert([{
          player1_id: player1Id,
          player2_id: player2Id,
          password,
          status: 'pending',
          player1_ready: false,
          player2_ready: false
        }]);

      if (matchError) throw matchError;

      // Nettoyer la file d'attente
      await supabase
        .from('queue')
        .delete()
        .in('player_id', [player1Id, player2Id]);

      return { password };
    } catch (error) {
      console.error(t('pvp_queue.errors.creating_match'), error);
      throw error;
    }
  };

  useEffect(() => {
    const checkExistingMatch = async () => {
      if (!user) return;

      const { data: existingMatch } = await supabase
        .from('matches')
        .select('password')
        .or(`player1_id.eq.${user.id},player2_id.eq.${user.id}`)
        .in('status', ['pending', 'active'])
        .limit(1)
        .single();

      // Utiliser getPathForLang pour préserver la langue actuelle
      if (existingMatch) {
        navigate(getPathForLang(`/pvp/match/${existingMatch.password}`));
        return;
      }
    };

    checkExistingMatch();
  }, [user, navigate, getPathForLang]);

  useEffect(() => {
    if (!inQueue || !user) return;

    const queueSubscription = supabase
      .channel('queue_changes')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'queue',
      }, async (payload) => {
        if (payload.eventType === 'INSERT' && payload.new.player_id !== user.id) {
          try {
            // Déterminer qui sera player1 (le plus ancien dans la file)
            const { data: queueOrder } = await supabase
              .from('queue')
              .select('player_id')
              .in('player_id', [user.id, payload.new.player_id])
              .order('created_at', { ascending: true });

            if (queueOrder && queueOrder.length === 2) {
              const [player1, player2] = queueOrder;
              // Seul le premier joueur crée le match
              if (player1.player_id === user.id) {
                const result = await createMatch(user.id, player2.player_id);
                if (result?.password) {
                  navigate(getPathForLang(`/pvp/match/${result.password}`));
                }
              }
            }
          } catch (error) {
            console.error(t('pvp_queue.errors.creating_match'), error);
          }
        }
      })
      .subscribe();

    // S'abonner aux nouveaux matchs
    const matchSubscription = supabase
      .channel('match_creation')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'matches',
      }, (payload) => {
        if (payload.new.player1_id === user?.id || payload.new.player2_id === user?.id) {
          setInQueue(false);
          navigate(getPathForLang(`/pvp/match/${payload.new.password}`));
        }
      })
      .subscribe();

    return () => {
      queueSubscription.unsubscribe();
      matchSubscription.unsubscribe();
    };
  }, [user, navigate, inQueue, getPathForLang]);

  const joinQueue = async () => {
    try {
      // Vérifier d'abord si le joueur a déjà une partie en cours
      const { data: existingMatch } = await supabase
        .from('matches')
        .select('password')
        .or(`player1_id.eq.${user?.id},player2_id.eq.${user?.id}`)
        .in('status', ['pending', 'active'])
        .limit(1)
        .single();

      // Utiliser getPathForLang pour préserver la langue actuelle
      if (existingMatch) {
        navigate(getPathForLang(`/pvp/match/${existingMatch.password}`));
        return;
      }

      setInQueue(true);

      // Vérifier d'abord s'il y a des joueurs en attente
      const { data: otherPlayers, error: searchError } = await supabase
        .from('queue')
        .select('player_id')
        .neq('player_id', user?.id)
        .limit(1);

      if (searchError) throw searchError;

      if (otherPlayers && otherPlayers.length > 0) {
        // Un autre joueur est trouvé, créer le match directement
        const result = await createMatch(user?.id!, otherPlayers[0].player_id);
        if (result?.password) {
          navigate(getPathForLang(`/pvp/match/${result.password}`));
        }
      } else {
        // Aucun joueur trouvé, rejoindre la file d'attente
        const { error: queueError } = await supabase
          .from('queue')
          .insert([{ player_id: user?.id }]);

        if (queueError) throw queueError;
      }
    } catch (error) {
      console.error(t('pvp_queue.errors.queue_error'), error);
      setInQueue(false);
    }
  };

  const leaveQueue = async () => {
    setInQueue(false);
    await supabase.from('queue').delete().eq('player_id', user?.id);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
      <PageTitle title={t('pvp_queue.title')} />
      <div className="max-w-lg w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center space-y-6">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <Swords className="w-8 h-8 text-primary-600" />
            <h1 className="text-2xl font-bold text-gray-900">{t('pvp_queue.mode_title')}</h1>
          </div>

          {!inQueue ? (
            <div className="space-y-4">
              <p className="text-gray-600">
                {t('pvp_queue.description')}
              </p>
              <button
                onClick={joinQueue}
                className="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {t('pvp_queue.find_opponent')}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col items-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-600 border-t-transparent"></div>
                <p className="text-gray-600 text-lg animate-pulse">
                  {t('pvp_queue.searching')}
                </p>
              </div>
              <button
                onClick={leaveQueue}
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 text-primary-600 hover:bg-primary-50 font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {t('pvp_queue.cancel_search')}
              </button>
              <p className="text-sm text-gray-500">
                {t('pvp_queue.estimated_wait')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
